/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

interface SyncMetadata {
  last_sync: any;
  orders_synced?: number;
  collections_synced?: number;  // Add this for collections
  sync_type: string;
}

export default function SyncStatus() {
  const [shopifySync, setShopifySync] = useState<SyncMetadata | null>(null);
  const [printfulSync, setPrintfulSync] = useState<SyncMetadata | null>(null);
  const [collectionsSync, setCollectionsSync] = useState<SyncMetadata | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSyncStatus = async () => {
      try {
        const db = getFirestore();
        
        const shopifyDoc = await getDoc(doc(db, 'sync_metadata', 'shopify'));
        const printfulDoc = await getDoc(doc(db, 'sync_metadata', 'printful'));
        const collectionsDoc = await getDoc(doc(db, 'sync_metadata', 'collections'));

        if (shopifyDoc.exists()) {
          setShopifySync(shopifyDoc.data() as SyncMetadata);
        }
        
        if (printfulDoc.exists()) {
          setPrintfulSync(printfulDoc.data() as SyncMetadata);
        }
         if (collectionsDoc.exists()) {
          setCollectionsSync(collectionsDoc.data() as SyncMetadata);
        }
      } catch (error) {
        console.error('Error fetching sync status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSyncStatus();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchSyncStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Never';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  if (loading) {
    return null; // Or a small loading indicator
  }

  return (
    <div className="sync-status">
      <div style={{ display: 'flex', flexDirection:'column', gap: '5px', flexWrap: 'wrap' }}>
        <div>
          <strong>Collections:</strong> {formatTimestamp(collectionsSync?.last_sync)}
          {collectionsSync?.collections_synced !== undefined && (
            <span style={{ marginLeft: '5px' }}>({collectionsSync.collections_synced} collections)</span>
          )}
        </div>
        <div>
          <strong>Shopify Data:</strong> {formatTimestamp(shopifySync?.last_sync)}
          {shopifySync?.orders_synced !== undefined && (
            <span style={{ marginLeft: '5px' }}>({shopifySync.orders_synced} orders)</span>
          )}
        </div>
        <div>
          <strong>Printful Data:</strong> {formatTimestamp(printfulSync?.last_sync)}
          {printfulSync?.orders_synced !== undefined && (
            <span style={{ marginLeft: '5px' }}>({printfulSync.orders_synced} orders)</span>
          )}
        </div>
      </div>
    </div>
  );
}